import { useQuery } from '@tanstack/react-query'
import { useAppContext } from '../redux/slices/appContext'
import { integrations, postNamedQuery } from '../service'
import { REDTAIL_INTEGRATION } from '../constants'
import { QUERY_KEYS } from './queryKeys'

export const useRedtailSyncStatus = (clientId, queryOptions) => {
  const { userId } = useAppContext()
  const { mapper, refetchInterval, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.redtailSyncStatus, userId, clientId],
    queryFn: async () => {
      const { data } = await integrations[REDTAIL_INTEGRATION].syncStatus(
        clientId
      )
      return data
    },
    refetchInterval,
    select: mapper,
    enabled
  })
}

export const useRedtailDomainQuery = (queryName, request, options = {}) => {
  const { userId, clientId } = useAppContext()
  const { enabled = true, ...restOptions } = options
  return useQuery({
    queryKey: [QUERY_KEYS.redtailDomainQuery, userId, clientId, queryName, request],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
        query: queryName,
        autoResolveConnection: true,
        clientId,
        request
      })
      return data
    },
    enabled: enabled && !!clientId,
    ...restOptions
  })
}

const BATCH_SIZE = 10

export const postBatchRedtailQueries = async (queries, { clientId, autoResolveConnection = true } = {}) => {
  const chunks = []
  for (let i = 0; i < queries.length; i += BATCH_SIZE) {
    chunks.push(queries.slice(i, i + BATCH_SIZE))
  }

  const allResults = []
  for (const chunk of chunks) {
    const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-queries', {
      queries: chunk,
      clientId,
      autoResolveConnection
    })
    allResults.push(...(data?.results ?? []))
  }

  return { results: allResults }
}

export const useRedtailClientLink = (clientId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.redtailFamilyContext, 'link', userId, clientId],
    queryFn: async () => {
      try {
        const { data } = await postNamedQuery('coreData', 'resolve-redtail-identifier', {
          type: 'client',
          id: clientId
        })
        return { linked: true, externalId: data.externalId }
      } catch (err) {
        if (err?.response?.status === 400) {
          return { linked: false, externalId: null }
        }
        throw err
      }
    },
    enabled: !!clientId
  })
}

export const useRedtailFamilyContext = (clientIdOverride) => {
  const { userId, clientId: contextClientId } = useAppContext()
  const clientId = clientIdOverride ?? contextClientId

  const link = useRedtailClientLink(clientId)
  const familyId = link.data?.externalId ?? null

  const membersQuery = useQuery({
    queryKey: [QUERY_KEYS.redtailFamilyContext, 'members', userId, clientId, familyId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
        query: 'listFamilyMembers',
        autoResolveConnection: true,
        clientId,
        request: { familyId }
      })
      return data?.family_members ?? data?.members ?? []
    },
    enabled: !!familyId
  })

  return {
    isLoading: link.isLoading || (!!familyId && membersQuery.isFetching),
    linked: link.data?.linked ?? null,
    familyId,
    members: membersQuery.data ?? [],
    error: link.error || membersQuery.error
  }
}
